import { campaignPages, normalizeRoutePath, publicPages, SITE_URL } from "./routeMetadata";

export type Choices = { ga4: boolean; clarity: boolean; rid: boolean };
export type Preferences = { version: 1; savedAt: number; expiresAt: number; choices: Choices };
export type ConsentSnapshot = { decided: boolean; choices: Choices; gpc: boolean; storageError: boolean };

export const CONSENT_KEY = "gw-marketing-privacy-v1";
export const CONSENT_TTL = 180 * 24 * 60 * 60 * 1000;
export const RID_KEY = "2b70aa0d2fba8972ead0afbc8a584fc3";
export const GA_ID = "G-5HCTQTZNL4";
export const CLARITY_ID = "xq4l2yzo5q";
export const OFF: Choices = { ga4: false, clarity: false, rid: false };
const failureCookie = "gw-privacy-storage-failed";

declare global {
  interface Window {
    GorillaRIDConsent?: boolean;
    __gorillaRidV1?: { disable: () => void };
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[][] };
    "ga-disable-G-5HCTQTZNL4"?: boolean;
  }
}

export function eligibleMarketingUrl(url: URL): boolean {
  // Exact origin, explicit allowlist, no previews, unknown industries or private routes.
  const path = normalizeRoutePath(url.pathname);
  return url.origin === SITE_URL && (Object.hasOwn(publicPages, path) || Object.hasOwn(campaignPages, path));
}

export function parsePreferences(raw: string | null, now = Date.now()): Preferences | null {
  try {
    const p = JSON.parse(raw ?? "null");
    if (!p || Object.keys(p).sort().join() !== "choices,expiresAt,savedAt,version" ||
        p.version !== 1 || !Number.isSafeInteger(p.savedAt) || !Number.isSafeInteger(p.expiresAt) ||
        p.savedAt > now || p.savedAt < 0 || p.expiresAt <= now ||
        p.expiresAt !== p.savedAt + CONSENT_TTL ||
        !p.choices || Object.keys(p.choices).sort().join() !== "clarity,ga4,rid" ||
        !Object.values(p.choices).every(v => typeof v === "boolean")) return null;
    return p;
  } catch { return null; }
}

export function signalChoices(choices: Choices, gpc: boolean): Choices {
  return gpc ? { ga4: choices.ga4, clarity: false, rid: false } : { ...choices };
}

/**
 * All loaders belong to one document lifecycle. Revocation cancels pending tags,
 * revokes RID synchronously and uses vendor controls BEFORE reloading. Removing
 * a script alone does not undo vendor listeners, requests or replay collection.
 */
export class MarketingConsent {
  private preferences: Preferences | null = null;
  private snapshot: ConsentSnapshot = { decided: false, choices: { ...OFF }, gpc: false, storageError: false };
  private active: Choices = { ...OFF };
  private scripts = new Map<keyof Choices, HTMLScriptElement>();
  private listeners = new Set<() => void>();
  private lastPage = "";
  private terminating = false;
  private timer?: number;
  private expiryTimer?: number;
  private cleanup?: () => void;

  constructor(
    private url = () => new URL(window.location.href),
    private reload = () => window.location.reload(),
    private navigate = (url: string) => window.location.assign(url),
  ) {}

  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  private gpc() {
    // No geographical exclusion; US opt-out signal conservatively honored globally.
    return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  }

  private notify(storageError = false) {
    const gpc = this.gpc();
    this.snapshot = {
      decided: !!this.preferences,
      // GA4 is public-site measurement, independent of the banner decision.
      // Keep the stored schema compatible; only Clarity/RID use saved grants.
      choices: { ...signalChoices(this.preferences?.choices ?? OFF, gpc), ga4: eligibleMarketingUrl(this.url()) },
      gpc, storageError,
    };
    window.clearTimeout(this.expiryTimer);
    if (this.preferences) {
      this.expiryTimer = window.setTimeout(() => this.routeChanged(),
        Math.min(this.preferences.expiresAt - Date.now(), 2_147_483_647));
    }
    this.listeners.forEach(fn => fn());
  }

  private read() {
    try {
      const raw = localStorage.getItem(CONSENT_KEY);
      let p = parsePreferences(raw);
      if (p && this.gpc() && (p.choices.clarity || p.choices.rid)) {
        // A recognized opt-out is durable; removing the signal must not silently
        // restore recording/association. It requires a later explicit choice.
        p = { ...p, choices: signalChoices(p.choices, true) };
        localStorage.setItem(CONSENT_KEY, JSON.stringify(p));
        if (localStorage.getItem(CONSENT_KEY) !== JSON.stringify(p)) throw new Error("Storage verification failed");
      }
      // Validate that storage is still usable, not merely readable after a failure.
      if (p) {
        const verifiedRaw = JSON.stringify(p);
        localStorage.setItem(CONSENT_KEY, verifiedRaw);
        if (localStorage.getItem(CONSENT_KEY) !== verifiedRaw) throw new Error("Storage verification failed");
      }
      const failed = document.cookie.split(";").some(c => c.trim() === `${failureCookie}=1`);
      this.preferences = failed ? null : p;
      this.notify(failed);
    } catch {
      this.preferences = null;
      this.markStorageFailure();
      this.notify(true);
    }
  }

  private markStorageFailure() {
    // Functional, non-identifying fail-closed guard if stale grants survived a
    // failed write. No authentication cookies or unrelated storage are touched.
    try { localStorage.removeItem(CONSENT_KEY); } catch { /* unavailable */ }
    try {
      document.cookie = `${failureCookie}=1; Path=/; Max-Age=${CONSENT_TTL / 1000}; SameSite=Lax; Secure`;
    } catch { /* browsers blocking all persistence remain denied in this document */ }
  }

  save = (choices: Choices) => {
    const now = Date.now();
    const next: Preferences = { version: 1, savedAt: now, expiresAt: now + CONSENT_TTL, choices: signalChoices(choices, this.gpc()) };
    try {
      const raw = JSON.stringify(next);
      localStorage.setItem(CONSENT_KEY, raw);
      if (localStorage.getItem(CONSENT_KEY) !== raw) throw new Error("Storage verification failed");
      document.cookie = `${failureCookie}=; Path=/; Max-Age=0; SameSite=Lax; Secure`;
      this.preferences = next;
      this.notify();
    } catch {
      this.preferences = null;
      this.markStorageFailure();
      this.notify(true);
    }
    this.apply();
  };

  start() {
    if (this.cleanup) return this.cleanup;
    this.read();
    this.apply();
    const refresh = () => { this.read(); this.apply(); };
    const storage = (event: StorageEvent) => {
      if (event.key === CONSENT_KEY || event.key === null) refresh();
    };
    window.addEventListener("storage", storage);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener("popstate", refresh);
    document.addEventListener("visibilitychange", refresh);
    this.timer = window.setInterval(refresh, 1000);
    // Prevent loaded vendors from seeing an SPA transition into a private URL.
    // Hard navigation ends the old runtime; private HTML contains no loaders.
    const push = history.pushState;
    const replace = history.replaceState;
    const guard = (original: typeof push): typeof push => (data, unused, href) => {
      if (href && this.scripts.size && !eligibleMarketingUrl(new URL(String(href), this.url()))) {
        this.stopAll();
        this.terminating = true;
        this.navigate(new URL(String(href), this.url()).href);
        return;
      }
      original.call(history, data, unused, href);
    };
    const guardedPush = guard(push), guardedReplace = guard(replace);
    history.pushState = guardedPush;
    history.replaceState = guardedReplace;
    this.cleanup = () => {
      window.removeEventListener("storage", storage);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener("popstate", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.clearInterval(this.timer);
      window.clearTimeout(this.expiryTimer);
      if (history.pushState === guardedPush) history.pushState = push;
      if (history.replaceState === guardedReplace) history.replaceState = replace;
      this.cleanup = undefined;
    };
    return this.cleanup;
  }

  routeChanged() {
    // Read again for cross-tab changes, expiry, failures and current browser signals.
    this.read();
    this.apply();
  }

  private bridge(granted: boolean) {
    window.GorillaRIDConsent = granted;
    window.dispatchEvent(new CustomEvent("gorilla-rid-consent", { detail: { granted } }));
    if (!granted) {
      try { sessionStorage.removeItem(`gw-rid:${RID_KEY}`); } catch { /* blocked */ }
    }
  }

  private append(tool: keyof Choices, src: string) {
    if (this.scripts.has(tool) || this.terminating) return;
    const script = document.createElement("script");
    script.async = true;
    script.src = src;
    script.dataset.gwMeasurement = tool;
    if (tool === "rid") script.dataset.key = RID_KEY;
    // No retries: blockers/CSP/provider failures must not create duplicate installs.
    script.onerror = () => { script.dataset.loadFailed = "true"; };
    this.scripts.set(tool, script);
    document.head.append(script);
  }

  private apply() {
    if (this.terminating) return;
    const next = eligibleMarketingUrl(this.url()) ? this.snapshot.choices : OFF;
    const revoked = (Object.keys(OFF) as (keyof Choices)[]).some(k => this.active[k] && !next[k]);
    if (revoked) {
      this.stopAll(next.ga4);
      this.terminating = true;
      this.reload();
      return;
    }
    this.active = { ...next };
    window[`ga-disable-${GA_ID}`] = !next.ga4;
    this.bridge(next.rid);
    if (next.ga4 && !this.scripts.has("ga4")) {
      window.dataLayer = [];
      window.gtag = function (..._args: unknown[]) { window.dataLayer!.push(arguments); };
      window.gtag("consent", "default", {
        analytics_storage: "granted", ad_storage: "denied",
        ad_user_data: "denied", ad_personalization: "denied",
      });
      window.gtag("set", { allow_google_signals: false, allow_ad_personalization_signals: false });
      window.gtag("js", new Date());
      window.gtag("config", GA_ID, {
        send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false,
        page_location: this.url().origin + this.url().pathname,
        page_referrer: "",
        cookie_expires: CONSENT_TTL / 1000, cookie_update: false,
      });
      this.append("ga4", `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
    }
    if (next.clarity && !this.scripts.has("clarity")) {
      const queue: NonNullable<Window["clarity"]> = (...args) => { queue.q!.push(args); };
      queue.q = [];
      window.clarity = queue;
      window.clarity("consentv2", { analytics_Storage: "granted", ad_Storage: "denied" });
      this.append("clarity", `https://www.clarity.ms/tag/${CLARITY_ID}`);
    }
    if (next.rid) this.append("rid", `${SITE_URL}/api/rid/collector-v1.js`);
    const page = this.url().origin + this.url().pathname;
    if (next.ga4 && this.lastPage !== page) {
      window.gtag?.("event", "page_view", { page_location: page, page_referrer: "" });
      this.lastPage = page;
    }
  }

  private stopAll(preserveGa4 = false) {
    this.bridge(false);
    try { window.__gorillaRidV1?.disable(); } catch { /* still reload */ }
    window[`ga-disable-${GA_ID}`] = !preserveGa4;
    // ga-disable is the no-data switch. Consent denial alone permits cookieless
    // pings; Clarity denial also permits no-consent recording, hence the reload.
    try {
      if (!preserveGa4 && this.scripts.has("ga4")) window.gtag?.("consent", "update", {
        analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied",
      });
      if (this.scripts.has("clarity")) {
        window.clarity?.("consentv2", { analytics_Storage: "denied", ad_Storage: "denied" });
        window.clarity?.("consent", false);
        window.clarity?.("stop");
      }
    } catch { /* vendor failure must not prevent shutdown */ }
    for (const script of this.scripts.values()) {
      if (preserveGa4 && script.dataset.gwMeasurement === "ga4") continue;
      script.onload = script.onerror = null;
      script.remove();
    }
    this.clearMeasurementCookies(preserveGa4);
    this.active = { ...OFF, ga4: preserveGa4 };
  }

  private clearMeasurementCookies(preserveGa4 = false) {
    // Only known optional tool cookies; never clear Clerk/auth or all storage.
    const names = document.cookie.split(";").map(v => v.trim().split("=")[0])
      .filter(name => (!preserveGa4 && (name === "_ga" || name.startsWith("_ga_"))) || name === "_clck" || name === "_clsk");
    const host = window.location.hostname;
    for (const name of names) {
      for (const domain of ["", host, `.${host}`]) {
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ""}`;
      }
    }
  }
}
